import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import './utils/axiosConfig'; // Import axios configuration
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { PinnedItemsProvider } from './contexts/PinnedItemsContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import Dashboard from './components/pages/Dashboard';
import LoginPage from './components/pages/LoginPage';
import YAMLPage from './components/pages/YAMLPage';
import SchoolForm from './components/pages/SchoolForm';
import SchoolEditForm from './components/pages/SchoolEditForm';
import MySchoolRedirect from './components/pages/MySchoolRedirect';
import TeacherForm from './components/pages/TeacherForm';
import TeacherEditForm from './components/pages/TeacherEditForm';
import SubjectForm from './components/pages/SubjectForm';
import SubjectEditForm from './components/pages/SubjectEditForm';

import LearnerForm from './components/pages/LearnerForm';
import LearnerEditForm from './components/pages/LearnerEditForm';
import SchoolClassForm from './components/pages/SchoolClassForm';
import SchoolClassEditForm from './components/pages/SchoolClassEditForm';
import AssessmentForm from './components/pages/AssessmentForm';
import AssessmentEditForm from './components/pages/AssessmentEditForm';
import RubricForm from './components/pages/RubricForm';
import RubricEditForm from './components/pages/RubricEditForm';
// NOTE: Grades removed from UI - grades are created automatically when grading submissions
// import GradeForm from './components/pages/GradeForm';
// import GradeEditForm from './components/pages/GradeEditForm';
import ConsentForm from './components/pages/ConsentForm';
import ConsentEditForm from './components/pages/ConsentEditForm';
import PrivacyPolicyForm from './components/pages/PrivacyPolicyForm';
import PrivacyPolicyEditForm from './components/pages/PrivacyPolicyEditForm';
import FastMarkPage from './components/pages/FastMarkPage';
import FastMarkPipelinePage from './components/pages/FastMarkPipelinePage';
import RubricAIAssistant from './components/pages/RubricAIAssistant';
import RubricStudioHome from './components/pages/RubricStudioHome';
import RubricStudio from './components/pages/RubricStudio';
import PapergenStudio from './components/pages/PapergenStudio';
import PapergenManagement from './components/pages/PapergenManagement';
import MoodleImportPage from './components/pages/MoodleImportPage';
import SettingsPage from './components/pages/SettingsPage';

import SubmissionMarkingPage from './components/pages/SubmissionMarkingPage';
import AssessmentGradesPage from './components/pages/AssessmentGradesPage';
import AssessmentReportList from './components/pages/AssessmentReportList';
import SubmissionsPage from './components/pages/SubmissionsPage';
import LearnerDetailPage from './components/pages/LearnerDetailPage';
import ClassEnrollmentPage from './components/pages/ClassEnrollmentPage';
import PrintClassListsPage from './components/pages/PrintClassListsPage';
import LearnerSubmitPage from './components/pages/LearnerSubmitPage';
import ClassDetailPage from './components/pages/ClassDetailPage';
import ClassContentPage from './components/pages/ClassContentPage';
import SoothInboxPage from './components/pages/SoothInboxPage';
import SoothRulesPage from './components/pages/SoothRulesPage';
import SoothConnectorsPage from './components/pages/SoothConnectorsPage';
import CommentGenerationPage from './pages/CommentGenerationPage';
import CommentTemplateManagementPage from './pages/CommentTemplateManagementPage';
import CommentGenPage from './components/pages/CommentGenPage';
import CurriculumReferencePage from './components/pages/CurriculumReferencePage';
import AssignmentsPage from './pages/AssignmentsPage';
import AssignmentDetailPage from './pages/AssignmentDetailPage';
import LaTeXReportGenerationPage from './pages/LaTeXReportGenerationPage';
import AssistantPage from './components/pages/AssistantPage';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
  },
});

const DRAWER_WIDTH = 240;

/**
 * AssignmentDetailRoute - bridges the legacy AssignmentDetailPage (.jsx),
 * which still takes token/user as props, to the AuthContext the rest of the
 * app uses. Rendered inside AuthProvider, so useAuth() is safe here.
 */
const AssignmentDetailRoute: React.FC<{ isCreating?: boolean }> = ({ isCreating }) => {
  const { user, token } = useAuth();
  return <AssignmentDetailPage token={token} user={user} isCreating={isCreating} />;
};

/**
 * AppContent - Inner component that has access to AuthContext
 * Conditionally renders Navbar only when authenticated
 */
const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(true);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Only show Navbar when authenticated */}
      {isAuthenticated && (
        <Navbar drawerOpen={drawerOpen} onDrawerToggle={handleDrawerToggle} />
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: 0,
          pt: isAuthenticated ? '64px' : 0,
        }}
      >
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/assistant" element={<ProtectedRoute requireRole={['teacher', 'admin']}><AssistantPage /></ProtectedRoute>} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

              {/* Sooth - Communications (Teachers and Admins) */}
              <Route path="/sooth" element={<ProtectedRoute requireRole={['teacher', 'admin']}><SoothInboxPage /></ProtectedRoute>} />
              <Route path="/sooth/rules" element={<ProtectedRoute requireRole={['teacher', 'admin']}><SoothRulesPage /></ProtectedRoute>} />
              <Route path="/sooth/connectors" element={<ProtectedRoute requireRole={['teacher', 'admin']}><SoothConnectorsPage /></ProtectedRoute>} />

              {/* Teacher Tools */}
              <Route path="/comment-gen" element={<ProtectedRoute requireRole={['teacher', 'admin']}><CommentGenPage /></ProtectedRoute>} />
              <Route path="/curriculum-reference" element={<ProtectedRoute><CurriculumReferencePage /></ProtectedRoute>} />
              <Route path="/fast-mark" element={<ProtectedRoute requireRole={['teacher', 'admin']}><FastMarkPage /></ProtectedRoute>} />
              <Route path="/fast-mark-pipeline" element={<ProtectedRoute requireRole={['teacher', 'admin']}><FastMarkPipelinePage /></ProtectedRoute>} />
              <Route path="/submissions/:id/mark" element={<ProtectedRoute requireRole={['teacher', 'admin', 'student']}><SubmissionMarkingPage /></ProtectedRoute>} />
              <Route path="/assessments/:id/grades" element={<ProtectedRoute requireRole={['teacher', 'admin']}><AssessmentGradesPage /></ProtectedRoute>} />
              <Route path="/assessment-reporting" element={<ProtectedRoute requireRole={['teacher', 'admin', 'school_admin']}><AssessmentReportList /></ProtectedRoute>} />
              <Route path="/moodle-import" element={<ProtectedRoute requireRole={['teacher', 'admin']}><MoodleImportPage /></ProtectedRoute>} />
              <Route path="/rubric-ai-assistant" element={<ProtectedRoute requireRole={['teacher', 'admin']}><RubricAIAssistant /></ProtectedRoute>} />
              <Route path="/rubric-studio" element={<ProtectedRoute requireRole={['teacher', 'admin']}><RubricStudioHome /></ProtectedRoute>} />
              <Route path="/rubric-studio/workspace" element={<ProtectedRoute requireRole={['teacher', 'admin']}><RubricStudio /></ProtectedRoute>} />
              <Route path="/papergen/studio" element={<ProtectedRoute requireRole={['teacher', 'admin']}><PapergenStudio /></ProtectedRoute>} />
              <Route path="/papergen/management" element={<ProtectedRoute requireRole={['teacher', 'admin']}><PapergenManagement /></ProtectedRoute>} />

              <Route path="/assignments" element={<ProtectedRoute requireRole={['teacher', 'admin']}><AssignmentsPage /></ProtectedRoute>} />
              <Route path="/assignments/new" element={<ProtectedRoute requireRole={['teacher', 'admin']}><AssignmentDetailRoute isCreating /></ProtectedRoute>} />
              <Route path="/assignments/:assignmentId" element={<ProtectedRoute requireRole={['teacher', 'admin']}><AssignmentDetailRoute /></ProtectedRoute>} />
              <Route path="/assignments/:assignmentId/generate-report" element={<ProtectedRoute requireRole={['teacher', 'admin']}><LaTeXReportGenerationPage /></ProtectedRoute>} />

              {/* Comment Generator - Teacher Tools */}
              <Route path="/comment-generation" element={<ProtectedRoute requireRole={['teacher', 'admin']}><CommentGenerationPage /></ProtectedRoute>} />
              <Route path="/comment-templates" element={<ProtectedRoute requireRole={['teacher', 'admin']}><CommentTemplateManagementPage /></ProtectedRoute>} />

              <Route path="/settings" element={<ProtectedRoute requireRole={['admin']}><SettingsPage /></ProtectedRoute>} />

              {/* Form pages - Create (Teacher/Admin only) */}
              <Route path="/schools/new" element={<ProtectedRoute requireRole={['teacher', 'admin']}><SchoolForm /></ProtectedRoute>} />
              <Route path="/teachers/new" element={<ProtectedRoute requireRole={['teacher', 'admin']}><TeacherForm /></ProtectedRoute>} />
              <Route path="/learners/new" element={<ProtectedRoute requireRole={['teacher', 'admin']}><LearnerForm /></ProtectedRoute>} />
              <Route path="/classes/new" element={<ProtectedRoute requireRole={['teacher', 'admin']}><SchoolClassForm /></ProtectedRoute>} />
              <Route path="/assessments/new" element={<ProtectedRoute requireRole={['teacher', 'admin']}><AssessmentForm /></ProtectedRoute>} />
              <Route path="/rubrics/new" element={<ProtectedRoute requireRole={['teacher', 'admin']}><RubricForm /></ProtectedRoute>} />
              {/* Grades route removed - grades are created when grading submissions, not manually */}
              {/* <Route path="/grades/new" element={<ProtectedRoute requireRole={['teacher', 'admin']}><GradeForm /></ProtectedRoute>} /> */}
              <Route path="/consents/new" element={<ProtectedRoute requireRole={['teacher', 'admin']}><ConsentForm /></ProtectedRoute>} />
              <Route path="/privacy-policies/new" element={<ProtectedRoute requireRole={['teacher', 'admin']}><PrivacyPolicyForm /></ProtectedRoute>} />

              {/* Form pages - Edit (Teacher/Admin only) */}
              <Route path="/schools/:id/edit" element={<ProtectedRoute requireRole={['teacher', 'admin']}><SchoolEditForm /></ProtectedRoute>} />
              {/* Administration → My School (caliper-navigation.yaml). Redirects
                  to the current user's school edit page; admins without a
                  Teacher row land on /schools and pick from the list. */}
              <Route path="/my-school" element={<ProtectedRoute requireRole={['teacher', 'admin']}><MySchoolRedirect /></ProtectedRoute>} />
              <Route path="/teachers/:id/edit" element={<ProtectedRoute requireRole={['teacher', 'admin']}><TeacherEditForm /></ProtectedRoute>} />
              <Route path="/learners/:id/edit" element={<ProtectedRoute requireRole={['teacher', 'admin']}><LearnerEditForm /></ProtectedRoute>} />
              <Route path="/classes/:id/edit" element={<ProtectedRoute requireRole={['teacher', 'admin']}><SchoolClassEditForm /></ProtectedRoute>} />
              <Route path="/classes/:id/enrollment" element={<ProtectedRoute requireRole={['teacher', 'admin']}><ClassEnrollmentPage /></ProtectedRoute>} />
              <Route path="/classes/print" element={<ProtectedRoute requireRole={['teacher', 'admin']}><PrintClassListsPage /></ProtectedRoute>} />
              <Route path="/classes/:id/content" element={<ProtectedRoute requireRole={['student', 'teacher', 'admin']}><ClassContentPage /></ProtectedRoute>} />
              <Route path="/classes/:id" element={<ProtectedRoute requireRole={['teacher', 'admin', 'student']}><ClassDetailPage /></ProtectedRoute>} />
              <Route path="/assessments/:id/edit" element={<ProtectedRoute requireRole={['teacher', 'admin']}><AssessmentEditForm /></ProtectedRoute>} />
              <Route path="/rubrics/:id/edit" element={<ProtectedRoute requireRole={['teacher', 'admin']}><RubricEditForm /></ProtectedRoute>} />
              {/* <Route path="/grades/:id/edit" element={<ProtectedRoute requireRole={['teacher', 'admin']}><GradeEditForm /></ProtectedRoute>} /> */}
              <Route path="/consents/:id/edit" element={<ProtectedRoute requireRole={['teacher', 'admin']}><ConsentEditForm /></ProtectedRoute>} />
              <Route path="/privacy-policies/:id/edit" element={<ProtectedRoute requireRole={['teacher', 'admin']}><PrivacyPolicyEditForm /></ProtectedRoute>} />

              {/* Learner submission page (All authenticated users, backend filters by student role) */}
              <Route path="/submit" element={<ProtectedRoute><LearnerSubmitPage /></ProtectedRoute>} />

              {/* YAML-driven list pages (All authenticated users can view) */}
              <Route path="/schools" element={<ProtectedRoute><YAMLPage pageId="schools" /></ProtectedRoute>} />
              <Route path="/teachers" element={<ProtectedRoute><YAMLPage pageId="teachers" /></ProtectedRoute>} />
              <Route path="/learners" element={<ProtectedRoute><YAMLPage pageId="learners" /></ProtectedRoute>} />
              <Route path="/learners/:id" element={<ProtectedRoute><LearnerDetailPage /></ProtectedRoute>} />
              <Route path="/classes" element={<ProtectedRoute><YAMLPage pageId="classes" /></ProtectedRoute>} />
              <Route path="/subjects" element={<ProtectedRoute><YAMLPage pageId="subjects" /></ProtectedRoute>} />
              <Route path="/subjects/new" element={<ProtectedRoute requireRole={['teacher', 'admin']}><SubjectForm /></ProtectedRoute>} />
              <Route path="/subjects/:id/edit" element={<ProtectedRoute requireRole={['teacher', 'admin']}><SubjectEditForm /></ProtectedRoute>} />
              <Route path="/assessments" element={<ProtectedRoute><YAMLPage pageId="assessments" /></ProtectedRoute>} />
              <Route path="/rubrics" element={<ProtectedRoute><YAMLPage pageId="rubrics" /></ProtectedRoute>} />
              <Route path="/submissions" element={<ProtectedRoute><SubmissionsPage /></ProtectedRoute>} />
              {/* <Route path="/grades" element={<ProtectedRoute><YAMLPage pageId="grades" /></ProtectedRoute>} /> */}
              <Route path="/pat-assessments" element={<ProtectedRoute><YAMLPage pageId="pat-assessments" /></ProtectedRoute>} />
              <Route path="/pat-phases" element={<ProtectedRoute><YAMLPage pageId="pat-phases" /></ProtectedRoute>} />
              <Route path="/consents" element={<ProtectedRoute><YAMLPage pageId="consents" /></ProtectedRoute>} />
              <Route path="/privacy-policies" element={<ProtectedRoute><YAMLPage pageId="privacy-policies" /></ProtectedRoute>} />
        </Routes>
      </Box>
    </Box>
  );
};

/**
 * App - Main component that wraps everything with providers
 */
const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <ImpersonationProvider>
            <FavoritesProvider>
              <PinnedItemsProvider>
                <AppContent />
              </PinnedItemsProvider>
            </FavoritesProvider>
          </ImpersonationProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;
// Force rebuild test
